import { type AuthSessionSnapshot } from '@app/packages/api/AuthSessionManager'

/** Decodes a JWT's payload (second segment) without verifying the signature. */
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const binary = atob(padded)
    const bytes = Uint8Array.from(binary, c => c.charCodeAt(0))
    return JSON.parse(new TextDecoder().decode(bytes))
  } catch {
    return null
  }
}

/** Returns the JWT `exp` claim in milliseconds since epoch, or null when absent/invalid. */
export const getJwtExp = (token: string): number | null => {
  const exp = decodeJwtPayload(token)?.exp
  return typeof exp === 'number' ? exp * 1000 : null
}

/** True when the token carries an `exp` claim that is already in the past. Tokens without `exp` never expire. */
export const isJwtExpired = (token: string, now: number = Date.now()): boolean => {
  const exp = getJwtExp(token)
  return exp !== null && now >= exp
}

export const getJwtClientId = (token: string): string | null => {
  const clientId = decodeJwtPayload(token)?.client_id
  return typeof clientId === 'string' ? clientId : null
}

/** True when the token is an admin2 impersonation (supervision) token. */
export const isImpersonationToken = (token: string): boolean => getJwtClientId(token) === 'admin2_impersonation'

/**
 * Builds an {@link AuthSessionSnapshot} from a JWT access token, or returns null
 * when the token is not a JWT (e.g. a legacy token) or lacks the `exp`/`sub`
 * claims a snapshot needs. The backend emits `sub` as a number, so a numeric
 * `sub` is accepted and coerced to a string `user_id`.
 */
export const snapshotFromJwt = (token: string): AuthSessionSnapshot | null => {
  const exp = getJwtExp(token)
  const sub = decodeJwtPayload(token)?.sub
  if (exp === null || (typeof sub !== 'string' && typeof sub !== 'number')) return null
  return { access_token: token, expires_at: exp, user_id: String(sub) }
}
